import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { FiLogIn, FiLogOut } from 'react-icons/fi';
import { setIsLoggedIn, setIsAdmin } from '../../reduxToolkit/redux';
import './Navbar.scss';
import imgLogo from '../../images/icons/logo.png';

const MENU_LABEL_PRICING = '\uC694\uAE08\uC548\uB0B4';
const MENU_LABEL_SETUP = '\uC124\uCE58\uD30C\uC77C';
const ADMIN_PORTAL_PATH = '/admin_mathtailer';
const QUESTION_BANK_URL = 'https://que.xn--vl2b19ao0ssqc39r.kr/';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.appReducer.isLoggedIn);
  const userInfo = useSelector((state) => state.appReducer.userInfo);
  const isHomePage = location.pathname === '/';
  const isMarketingDetailPage = location.pathname === '/pricing' || location.pathname === '/setup-download';
  const canAccessAdminPortal = isLoggedIn && userInfo?.userId === 'admin_mathtailer';
  const activeMenu = location.pathname === '/'
    ? 'home'
    : location.pathname === '/pricing'
      ? 'pricing'
      : location.pathname === '/setup-download'
        ? 'setup-download'
        : '';

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  const handleQuestionBankClick = (e) => {
    const randomVersion = Math.random().toString(36).slice(2);
    e.currentTarget.href = `${QUESTION_BANK_URL}?v=${randomVersion}`;
    closeMobileMenu();
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    closeMobileMenu();
  }, [location]);

  function handleLogout() {
    dispatch(setIsLoggedIn(false));
    dispatch(setIsAdmin(false));
    navigate('/');
  }

  return (
    <>
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMarketingDetailPage ? 'route-blue' : ''} ${!isHomePage && !isMarketingDetailPage ? 'light-bg' : ''}`}>
      <div className="container">
        <Link to="/" className="logo" onClick={handleHomeClick}>
          <motion.div
            className="logo-icon"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="logo-row">
              <img src={imgLogo} alt="logo" className="logo-img" />
              <span className="logo-text">
                <span className="logo-math">매쓰</span>
                <span className="logo-gen">테일러</span>
              </span>
            </div>
            <span className="logo-slogan">AI Math Authoring Engine</span>
          </motion.div>
        </Link>

        <ul className="menu">
          <li className="menu-item">
            <Link
              to="/"
              className={`menu-link ${activeMenu === 'home' ? 'active' : ''}`}
              onClick={handleHomeClick}
            >
              HOME
            </Link>
          </li>

          <li className="menu-item">
            <Link
              to="/pricing"
              className={`menu-link ${activeMenu === 'pricing' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              {MENU_LABEL_PRICING}
            </Link>
          </li>

          <li className="menu-item">
            <Link
              to="/setup-download"
              className={`menu-link ${activeMenu === 'setup-download' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              {MENU_LABEL_SETUP}
            </Link>
          </li>
          
          <li className="menu-item">
            <Link
              to="/manual"
              className={`menu-link ${location.pathname === '/manual' ? 'active' : ''}`}
                onClick={closeMobileMenu}
            >
              매뉴얼
            </Link>
          </li>
          {isLoggedIn && (
            <li className="menu-item">
              <Link
                to="/myaccount"
                className={`menu-link ${location.pathname === '/myaccount' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                내 계정
              </Link>
            </li>
          )}
          <li className="menu-item">
            <Link
              to="/charge"
              className={`menu-link menu-link--charge ${location.pathname === '/charge' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              충전하기
            </Link>
          </li>
          <li className="menu-item">
            <a
              href={QUESTION_BANK_URL}
              className="menu-link menu-link--question-bank"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleQuestionBankClick}
            >
              문제은행
            </a>
          </li>
          {canAccessAdminPortal && (
            <li className="menu-item">
              <Link
                to={ADMIN_PORTAL_PATH}
                className="menu-link"
                onClick={closeMobileMenu}
              >
                관리자
              </Link>
            </li>
          )}
        </ul>

        {isLoggedIn ? (
          <button className="btn-logout" onClick={handleLogout}>로그아웃</button>
        ) : (
          <button className="btn-login" onClick={() => navigate('/login')}>로그인</button>
        )}
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <></>
        )}
      </AnimatePresence>
    </nav>

    {createPortal(
      <button 
        className={`toggle ${isMobileMenuOpen ? 'active' : ''} ${isScrolled ? 'scrolled' : ''} ${isMarketingDetailPage ? 'route-blue' : ''} ${!isHomePage && !isMarketingDetailPage ? 'light-bg' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>,
      document.body
    )}

    {createPortal(
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="mobile-menu-header">
                <div className="mobile-menu-brand">
                  <img src={imgLogo} alt="logo" className="mobile-menu-logo" />
                  <span className="mobile-menu-title">
                    <span className="t-math">매쓰</span><span className="t-gen">테일러</span>
                  </span>
                </div>
                <div className="mobile-menu-sub">AI Math Authoring Engine</div>
              </div>
            <ul className="mobile-list">
              <motion.li
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Link
                  to="/"
                  className={`mobile-link ${activeMenu === 'home' ? 'active' : ''}`}
                  onClick={handleHomeClick}
                >
                  HOME
                </Link>
              </motion.li>
              <motion.li 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Link
                  to="/pricing"
                  className={`mobile-link ${activeMenu === 'pricing' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {MENU_LABEL_PRICING}
                </Link>
              </motion.li>
              <motion.li
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Link
                  to="/setup-download"
                  className={`mobile-link ${activeMenu === 'setup-download' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {MENU_LABEL_SETUP}
                </Link>
              </motion.li>
              <motion.li 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.22 }}
              >
                <Link
                  to="/manual"
                  className={`mobile-link ${location.pathname === '/manual' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  매뉴얼
                </Link>
              </motion.li>
              {isLoggedIn && (
                <motion.li
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.24 }}
                >
                  <Link
                    to="/myaccount"
                    className={`mobile-link ${location.pathname === '/myaccount' ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    내 계정
                  </Link>
                </motion.li>
              )}
              <motion.li
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Link
                  to="/charge"
                  className={`mobile-link mobile-link--charge ${location.pathname === '/charge' ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  충전하기
                </Link>
              </motion.li>
              <motion.li
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.255 }}
              >
                <a
                  href={QUESTION_BANK_URL}
                  className="mobile-link mobile-link--question-bank"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleQuestionBankClick}
                >
                  문제은행
                </a>
              </motion.li>
              {canAccessAdminPortal && (
                <motion.li
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.255 }}
                >
                  <Link
                    to={ADMIN_PORTAL_PATH}
                    className="mobile-link"
                    onClick={closeMobileMenu}
                  >
                    관리자
                  </Link>
                </motion.li>
              )}
              <div className="mobile-menu-divider" />
              <motion.li 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.26 }}
              >
                {isLoggedIn ? (
                  <button className="mobile-link btn-mobile-logout" onClick={handleLogout}>
                    <FiLogOut size={17} />&nbsp;로그아웃
                  </button>
                ) : (
                  <button 
                    className="mobile-link btn-mobile-login" 
                    onClick={() => {
                      navigate('/login');
                      closeMobileMenu();
                    }}
                  >
                    <FiLogIn size={17} />로그인
                  </button>
                )}
              </motion.li>
            </ul>
          </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    )}
    </>
  );
};

export default Navbar;
